import { EmptyActionsIcon, IRowNode, InformationState } from '@access-erp/ui-components';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useGetPurchaseOrdersHistory } from '../../hooks/useGetPurchaseOrdersHistory';

import searchStyles from './history-search.module.scss';
import HistorySearchHeader from '@/components/common/HistorySearch/HistorySearchHeader';
import styles from './../../../../components/common/HistoryCard/history.module.scss';
import { GetPaginatedFilteredPurchaseOrders }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredPurchaseOrders.query';
import HistoryCard from '@/components/common/HistoryCard/HistoryCard';
import Spinner from '@/components/common/Spinner/Spinner';
import { User } from '@/hooks/identity/responseModels/user';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { useHistorySearch } from '@/pages/ContractDetails/RightPanel/History/hooks/useHistorySearch';

type PurchaseOrderWithId = GetPaginatedFilteredPurchaseOrders & {
  id: string;
};
interface HistoryProps {
  selectedPurchaseOrders: IRowNode<PurchaseOrderWithId>[];
}

export default function History({ selectedPurchaseOrders }: HistoryProps) {
  const { getPurchaseOrdersHistory, purchaseOrdersHistory, isLoading } =
    useGetPurchaseOrdersHistory();
  const { searchUsers, isLoadingSearchUsers } = useSearchUsers();
  const [users, setUsers] = useState<User[]>([]);
  const tabStyle = styles['tab-wrapper'];
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const {
    searchTerm,
    filteredHistoryData,
    handleSearchChange,
    dateFilter,
    handleDateFilterChange,
  } = useHistorySearch({
    auditHistoryData: (purchaseOrdersHistory as any) ?? [],
    isLoadingSearchUsers,
    users,
  });

  useEffect(() => {
    if (
      selectedPurchaseOrders &&
      selectedPurchaseOrders.length > 0 &&
      selectedPurchaseOrders[0]?.data?.id
    ) {
      getPurchaseOrdersHistory(selectedPurchaseOrders[0].data.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPurchaseOrders]);

  useEffect(() => {
    if (!isLoading && purchaseOrdersHistory?.length) {
      const getUsers = async () => {
        const userIds = purchaseOrdersHistory
          .map((history) => history.eventSourceId)
          .filter((id): id is string => typeof id === 'string' && !!id);
        const uniqueUserIds = new Set(userIds);
        const searchResult = await searchUsers({ ids: [...uniqueUserIds] });
        setUsers(searchResult);
      };

      getUsers();
    }
  }, [purchaseOrdersHistory, isLoading, searchUsers]);

  useLayoutEffect(() => {
    const el = wrapperRef.current as HTMLElement | null;
    const parent = el?.parentElement as HTMLElement | null;
    if (parent) {
      parent.style.paddingTop = '0px';
    }
  }, [purchaseOrdersHistory]);

  if (!selectedPurchaseOrders || selectedPurchaseOrders.length === 0) {
    return (
      <InformationState
        icon={<EmptyActionsIcon />}
        title="There is no data to show"
        description="Select a purchase order to see its history"
        marginTop={14.6}
      />
    );
  }

  if (Array.isArray(purchaseOrdersHistory) && purchaseOrdersHistory.length === 0) {
    return (
      <div className={tabStyle}>
        <div className="d-flex justify-content-center">Purchase order has no history yet</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={tabStyle}>
        <div className="d-flex justify-content-center mt-40">
          <Spinner />
        </div>
      </div>
    );
  }
  return (
    <div ref={wrapperRef} className="historyWrapper">
      <HistorySearchHeader
        title="History"
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        date={dateFilter.startDate}
        onDateChange={(d) => handleDateFilterChange(d, null)}
        showClear={!!dateFilter.startDate}
      />

      <div className={tabStyle}>
        {filteredHistoryData?.map((el) => {
          const user = users.find((u) => u.Id == el.eventSourceId);
          return (
            <div key={el.changeSetID + user?.Id} className="mb-40">
              <HistoryCard historyItem={el} user={user} isLoading={isLoadingSearchUsers} />
            </div>
          );
        })}
      </div>

      {(searchTerm.trim() || dateFilter.startDate) &&
        (filteredHistoryData?.length ?? 0) === 0 &&
        (purchaseOrdersHistory?.length ?? 0) > 0 && !isLoadingSearchUsers && (
          <div className={searchStyles.noResultsMessage}>
            <span>No history records found matching your filters.</span>
          </div>
      )}
    </div>
  );
}
